/**
 * ArticleScreen — `/library/:slug`, the only read-mode surface in the product.
 *
 * WHY IT LOOKS LIKE THIS. Someone lands here because they are about to spend a
 * large share of their savings and want to understand something first. That
 * makes comprehension and wayfinding the whole job, and the previous build was
 * losing both: 15.5px body type at `--g-ink-2` (≈4.6:1 on cream — legally
 * passing, miserable to read for three minutes), no visible sense of how long
 * the article was or where in it you were, and a table of contents whose active
 * item only ever changed when you clicked it.
 *
 * What it is now, in the counter-slip language:
 *
 *   • A SLIP HEAD. The title in the display voice, then a drawn three-cell
 *     label grid — Чтение / Разделы / Статья — with each measure in `.g-data`.
 *     Those three figures answer "how long is this", "how is it structured" and
 *     "where does it sit in the library" before a word of the article is read.
 *     The index position comes from the same `useArticleList` query the index
 *     screen already populated, so it is real data, not a decoration.
 *   • DRAWN ARMATURE. The desktop contents rail is separated by a real column
 *     rule; every section is separated by a real horizontal rule; the reading
 *     progress is drawn on the rule under the sticky breadcrumb rather than as
 *     a floating bar.
 *   • A READING MEASURE. 68ch column, 17px/1.75 body at FULL ink contrast,
 *     a larger lead paragraph, and headings with more space above than below so
 *     a section reads as a section.
 *   • FIGURES IN THE INSTRUMENT FACE. Every number and unit inside the prose is
 *     set in `.g-data` (see `figures.tsx`) — in a renovation article the number
 *     is the thing the reader came for.
 *   • CALLOUTS AS MARGIN NOTES. Not tinted cards: a recessed `--g-sunken` well
 *     bounded by rules, labelled Совет / Осторожно in small caps. Colour is
 *     never the only signal (WCAG 1.4.1) — the label carries the meaning.
 *
 * WAYFINDING MECHANICS. One rAF-throttled scroll listener registered in the
 * CAPTURE phase on `window` drives both the progress rule and the active
 * section. Capture is required because the app scrolls inside `<main>` (scroll
 * events do not bubble, but they do capture), and this way the screen does not
 * care which ancestor happens to be the scroller. A click on a contents entry
 * locks the scroll-spy briefly so smooth-scrolling cannot fight the selection.
 *
 * PRESERVED CONTRACTS: `article-screen`, `article-back`, `article-skeleton`,
 * `article-section-{i}`, `article-toc-desktop`, `article-toc-item-{i}`,
 * `article-callout-{info|warn}`, `article-ask`, `article-not-found`,
 * `article-not-found-cta`, `article-error`, `article-retry`, plus the /chat
 * seed state and the 404-vs-error split.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  Info,
  TriangleAlert,
} from "lucide-react";

import { Button, GMotif, Skeleton } from "@garden/ui";

import { ApiError } from "@/lib/api-client";
import {
  isCalloutBlock,
  type KbArticleDetail,
  type KbArticleSummary,
  type KbBlock,
} from "@/lib/garden-api";
import { iconProps } from "@/lib/icons";
import { notify } from "@/lib/notify";
import { useGardenMotion } from "@/lib/use-garden-motion";

import { withFigures } from "./figures";
import {
  registerLibraryI18n,
  resolveKbLanguage,
  useArticle,
  useArticleList,
} from "./api";

/** Route path back to the article index. */
export const LIBRARY_LIST_PATH = "/library";

/** How long the scroll-spy defers to a contents click (ms). */
const SPY_LOCK_MS = 700;

/** Viewport offset (px) at which a section counts as "the one you are reading". */
const SPY_THRESHOLD = 140;

/** A heading and everything that belongs under it. */
interface ArticleSection {
  heading: string | null;
  blocks: KbBlock[];
}

/**
 * Nearest scrolling ancestor, or null when the document itself scrolls. The
 * app renders screens inside `<main class="overflow-y-auto">`, so `window`
 * scroll geometry alone would measure the wrong box.
 */
function findScrollParent(start: HTMLElement | null): HTMLElement | null {
  let node = start?.parentElement ?? null;
  while (node !== null) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Group the flat block list into heading-led sections.
 *
 * The KB serializer emits the article's intro paragraph as `{h: "", p: …}` —
 * an EMPTY heading. Rendering that verbatim (which production did) puts a
 * blank `<h2>` in the accessibility tree at the top of most articles. An empty
 * or whitespace-only `h` is therefore normalised to "no heading": the intro
 * becomes a headingless lead section, exactly as it reads on the page.
 */
function toSections(body: KbBlock[]): ArticleSection[] {
  const sections: ArticleSection[] = [];
  for (const block of body) {
    if (!isCalloutBlock(block)) {
      const heading = block.h.trim();
      sections.push({ heading: heading === "" ? null : heading, blocks: [block] });
      continue;
    }
    const current = sections[sections.length - 1];
    if (current === undefined) {
      sections.push({ heading: null, blocks: [block] });
    } else {
      current.blocks.push(block);
    }
  }
  return sections;
}

/**
 * Sticky breadcrumb + the reading-progress rule. The bar sits on the ground
 * colour rather than on a raised surface so it reads as the top of the same
 * slip; the only line under it is the drawn rule, which doubles as the
 * progress track.
 */
function ArticleChrome({
  title,
  progress,
  showProgress,
  children,
}: {
  title: string | null;
  progress: number;
  showProgress: boolean;
  children: ReactElement;
}): ReactElement {
  const { t } = useTranslation("library");
  return (
    <div data-testid="article-screen" className="min-h-full bg-[var(--g-ground)]">
      <div className="sticky top-0 z-10 bg-[var(--g-ground)]">
        <div className="mx-auto flex max-w-[70rem] items-center gap-2.5 px-5 py-2 md:px-8">
          <Link
            to={LIBRARY_LIST_PATH}
            aria-label={t("back")}
            data-testid="article-back"
            className="-ml-2.5 grid size-11 shrink-0 place-items-center rounded-[var(--g-radius)] text-foreground transition-colors hover:bg-[var(--g-sunken)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ArrowLeft {...iconProps("md")} />
          </Link>
          <span className="shrink-0 text-[11px] font-bold uppercase tracking-[0.09em] text-[var(--g-ink-2)]">
            {t("crumbs")}
          </span>
          <span aria-hidden="true" className="shrink-0 text-[var(--g-ink-2)]">
            /
          </span>
          <span className="truncate text-[13px] font-semibold text-foreground">
            {title ?? t("title")}
          </span>
        </div>
        {showProgress ? (
          <div
            role="progressbar"
            aria-label={t("progressLabel")}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            data-testid="article-progress"
            className="h-[2px] w-full bg-[var(--g-rule)]"
          >
            <div
              className="h-full origin-left bg-primary"
              style={{ transform: `scaleX(${String(progress)})` }}
            />
          </div>
        ) : (
          <div className="h-px w-full bg-[var(--g-rule)]" />
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * Empty-ish states (not-found / error) in the same ruled-slip language the
 * index uses: strong top rule, display headline, one plain line, one action.
 */
function ArticleSlip({
  testId,
  role,
  title,
  body,
  action,
  motif = false,
}: {
  testId: string;
  role: "status" | "alert";
  title: string;
  body: string;
  action: ReactElement;
  motif?: boolean;
}): ReactElement {
  return (
    <div className="mx-auto max-w-[70rem] px-5 pb-24 pt-8 md:px-8 md:pt-12">
      <div
        data-testid={testId}
        role={role}
        className="relative overflow-hidden border-t border-[var(--g-rule-strong)] pb-10 pt-9 md:pb-14 md:pt-12"
      >
        {motif && (
          <GMotif
            tone="primary"
            opacity={0.16}
            className="pointer-events-none absolute -right-6 -top-4 h-40 w-56 md:h-52 md:w-72"
          />
        )}
        <div className="relative max-w-[42ch]">
          <p className="font-display m-0 text-[21px] leading-[1.2] text-foreground md:text-[26px]">
            {title}
          </p>
          <p className="mt-3 text-[15px] leading-[1.65] text-[var(--g-ink-2)]">{body}</p>
          <div className="mt-6">{action}</div>
        </div>
      </div>
    </div>
  );
}

/** Loading placeholder — the reading rhythm, not a generic block stack. */
function ArticleSkeleton(): ReactElement {
  return (
    <div
      data-testid="article-skeleton"
      aria-hidden="true"
      className="mx-auto max-w-[70rem] px-5 pb-24 pt-8 md:px-8 md:pt-12"
    >
      <div className="max-w-[68ch]">
        <Skeleton w="86%" h={34} r={8} />
        <div className="mt-3">
          <Skeleton w="52%" h={34} r={8} />
        </div>
        <div className="mt-7 flex gap-8 border-y border-[var(--g-rule)] py-3.5">
          <Skeleton w={68} h={14} r={5} />
          <Skeleton w={68} h={14} r={5} />
          <Skeleton w={68} h={14} r={5} />
        </div>
        <div className="mt-9 flex flex-col gap-3.5">
          <Skeleton w="100%" h={15} r={5} />
          <Skeleton w="97%" h={15} r={5} />
          <Skeleton w="91%" h={15} r={5} />
          <Skeleton w="66%" h={15} r={5} />
        </div>
        <div className="mt-10">
          <Skeleton w="44%" h={22} r={6} />
          <div className="mt-5 flex flex-col gap-3.5">
            <Skeleton w="100%" h={15} r={5} />
            <Skeleton w="94%" h={15} r={5} />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * A callout as a margin note on the slip: recessed well, rules top and bottom,
 * a small-caps label that names the tone in words. Full-bleed on mobile so it
 * reads as a different kind of thing from the paragraph above it.
 */
function Note({
  variant,
  text,
  blockKey,
}: {
  variant: "info" | "warn";
  text: string;
  blockKey: string;
}): ReactElement {
  const { t } = useTranslation("library");
  const isWarn = variant === "warn";
  return (
    <aside
      data-testid={`article-callout-${variant}`}
      className="-mx-5 my-7 border-y border-[var(--g-rule)] bg-[var(--g-sunken)] px-5 py-4 md:mx-0 md:my-8 md:px-6 md:py-5"
    >
      {/* The WORD carries the tone, not the colour: `--g-warn` at 11px would
          sit near 3.3:1 on the sunken well, so the label is full ink and only
          the icon is tinted (icons need 3:1, and the label is never the sole
          signal — WCAG 1.4.1). */}
      <p className="m-0 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.09em] text-foreground">
        <span className={isWarn ? "text-[var(--g-warn)]" : "text-primary"}>
          {isWarn ? (
            <TriangleAlert {...iconProps("sm")} />
          ) : (
            <Info {...iconProps("sm")} />
          )}
        </span>
        {isWarn ? t("noteWarn") : t("noteInfo")}
      </p>
      <p className="m-0 mt-2 text-[15.5px] leading-[1.65] text-foreground md:text-[16px]">
        {withFigures(text, blockKey)}
      </p>
    </aside>
  );
}

/** The contents rail (desktop) — a real column with a real rule beside it. */
function ContentsRail({
  toc,
  active,
  onSelect,
}: {
  toc: string[];
  active: number;
  onSelect: (index: number) => void;
}): ReactElement {
  const { t } = useTranslation("library");
  return (
    <aside
      data-testid="article-toc-desktop"
      className="hidden md:sticky md:top-20 md:block md:self-start md:border-r md:border-[var(--g-rule)] md:pr-6"
    >
      <p className="m-0 mb-3 text-[11px] font-bold uppercase tracking-[0.09em] text-[var(--g-ink-2)]">
        {t("toc")}
      </p>
      <nav className="flex flex-col">
        {toc.map((entry, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onSelect(i)}
            aria-current={active === i ? "true" : undefined}
            data-testid={`article-toc-item-${i}`}
            className={
              "rounded-[var(--g-radius-sm)] px-2.5 py-2 text-left text-[13.5px] leading-snug transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
              (active === i
                ? "bg-[var(--g-sunken)] font-bold text-foreground"
                : "text-[var(--g-ink-2)] hover:text-foreground")
            }
          >
            {entry}
          </button>
        ))}
      </nav>
    </aside>
  );
}

/**
 * The contents on mobile: a disclosure, not a row of scrolling pills. Russian
 * and Kyrgyz section headings are full sentences — truncated into pills they
 * stop being wayfinding and become decoration. Collapsed by default so it costs
 * one line of the reading surface.
 */
function ContentsDisclosure({
  toc,
  active,
  onSelect,
}: {
  toc: string[];
  active: number;
  onSelect: (index: number) => void;
}): ReactElement {
  const { t } = useTranslation("library");
  const [open, setOpen] = useState(false);
  return (
    // Sits flush under the measure strip (no gap, no second top rule) so the
    // head of the article reads as one continuous ruled stack.
    <div className="border-b border-[var(--g-rule)] md:hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="article-toc-mobile-list"
        className="flex min-h-[44px] w-full items-center justify-between gap-3 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex min-w-0 items-baseline gap-2.5">
          <span className="shrink-0 text-[11px] font-bold uppercase tracking-[0.09em] text-[var(--g-ink-2)]">
            {t("toc")}
          </span>
          <span className="truncate text-[13.5px] font-semibold text-foreground">
            {toc[active] ?? toc[0]}
          </span>
        </span>
        <ChevronDown
          {...iconProps("md")}
          className={
            "shrink-0 text-[var(--g-ink-2)] transition-transform motion-reduce:transition-none " +
            (open ? "rotate-180" : "")
          }
        />
      </button>
      {open && (
        <ol
          id="article-toc-mobile-list"
          className="g-ruled m-0 list-none border-t border-[var(--g-rule)] p-0"
        >
          {toc.map((entry, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onSelect(i);
                }}
                aria-current={active === i ? "true" : undefined}
                className={
                  "flex min-h-[44px] w-full items-center gap-3 py-2.5 text-left text-[14.5px] leading-snug focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
                  (active === i ? "font-bold text-foreground" : "text-[var(--g-ink-2)]")
                }
              >
                <span className="g-data w-6 shrink-0 text-[12px] text-[var(--g-ink-2)]">
                  {i + 1}
                </span>
                {entry}
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** One labelled measure in the slip head. */
function MeasureCell({
  label,
  value,
  unit,
}: {
  label: string;
  value: string;
  unit?: string;
}): ReactElement {
  return (
    <div className="px-4 first:pl-0 last:pr-0">
      <p className="m-0 text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--g-ink-2)]">
        {label}
      </p>
      <p className="m-0 mt-1 flex items-baseline gap-1.5">
        <span className="g-data text-[15px] font-medium text-[var(--g-figure)] md:text-[16px]">
          {value}
        </span>
        {unit !== undefined && (
          <span className="text-[12px] text-[var(--g-ink-2)]">{unit}</span>
        )}
      </p>
    </div>
  );
}

interface ArticleBodyProps {
  article: KbArticleDetail;
  siblings: KbArticleSummary[];
  onProgress: (value: number) => void;
}

/** The reader itself: slip head, contents, prose, ask band, prev/next. */
function ArticleBody({
  article,
  siblings,
  onProgress,
}: ArticleBodyProps): ReactElement {
  const { t } = useTranslation("library");
  const navigate = useNavigate();
  const { reduced } = useGardenMotion();
  const [active, setActive] = useState(0);
  const articleRef = useRef<HTMLElement | null>(null);
  const sectionRefs = useRef<Array<HTMLElement | null>>([]);
  const spyLockUntil = useRef(0);

  const sections = useMemo(() => toSections(article.body), [article.body]);
  const toc = article.toc;
  const showToc = toc.length >= 2;

  const goToSection = useCallback(
    (index: number): void => {
      spyLockUntil.current = Date.now() + SPY_LOCK_MS;
      setActive(index);
      sectionRefs.current[index]?.scrollIntoView({
        behavior: reduced ? "auto" : "smooth",
        block: "start",
      });
    },
    [reduced],
  );

  // One capture-phase scroll listener drives BOTH the progress rule and the
  // active section. Capture (not bubble) because the scroller is an ancestor
  // `<main>`, and scroll events do not bubble.
  useEffect(() => {
    const node = articleRef.current;
    if (node === null) return;
    const scroller = findScrollParent(node);
    let frame = 0;

    const measure = (): void => {
      frame = 0;
      const rect = node.getBoundingClientRect();
      const viewTop = scroller === null ? 0 : scroller.getBoundingClientRect().top;
      const viewHeight =
        scroller === null ? window.innerHeight : scroller.clientHeight;
      const scrollable = rect.height - viewHeight;
      const passed = viewTop - rect.top;
      const ratio =
        scrollable <= 0 ? (rect.bottom <= viewTop + viewHeight ? 1 : 0) : passed / scrollable;
      onProgress(Math.min(1, Math.max(0, ratio)));

      if (Date.now() < spyLockUntil.current) return;
      let current = 0;
      sectionRefs.current.forEach((section, index) => {
        if (section === null) return;
        if (section.getBoundingClientRect().top - viewTop <= SPY_THRESHOLD) {
          current = index;
        }
      });
      setActive(current);
    };

    const onScroll = (): void => {
      if (frame === 0) frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [article.slug, onProgress]);

  const minutes = article.reading_time_min ?? 1;
  const position = siblings.findIndex((a) => a.slug === article.slug);
  const previous = position > 0 ? siblings[position - 1] : undefined;
  const next =
    position >= 0 && position < siblings.length - 1
      ? siblings[position + 1]
      : undefined;

  return (
    <div
      className={
        "mx-auto grid max-w-[70rem] px-5 pb-24 pt-6 md:gap-x-10 md:px-8 md:pb-20 md:pt-10 " +
        (showToc ? "md:grid-cols-[13rem_minmax(0,1fr)]" : "md:grid-cols-1")
      }
    >
      {showToc && (
        <ContentsRail toc={toc} active={active} onSelect={goToSection} />
      )}

      <article ref={articleRef} className="min-w-0 max-w-[68ch]">
        <h1 className="font-display m-0 text-[27px] leading-[1.14] text-foreground md:text-[38px]">
          {article.title}
        </h1>

        {/* The slip head: three drawn measures, each figure in the instrument
            face. Answers how long / how structured / where in the library. */}
        <div className="mt-6 flex border-y border-[var(--g-rule)] py-3.5 md:mt-7 [&>*+*]:border-l [&>*+*]:border-[var(--g-rule)]">
          <MeasureCell
            label={t("labelRead")}
            value={String(minutes)}
            unit={t("minUnit")}
          />
          {toc.length > 0 && (
            <MeasureCell label={t("labelSections")} value={String(toc.length)} />
          )}
          {position >= 0 && siblings.length > 0 && (
            <MeasureCell
              label={t("labelArticle")}
              value={`${String(position + 1)}/${String(siblings.length)}`}
            />
          )}
        </div>

        {showToc && (
          <ContentsDisclosure toc={toc} active={active} onSelect={goToSection} />
        )}

        <div className="mt-8 md:mt-10">
          {sections.map((section, sectionIndex) => {
            const tocIndex =
              section.heading === null ? -1 : toc.indexOf(section.heading);
            return (
              <section
                key={sectionIndex}
                ref={(el) => {
                  if (tocIndex >= 0) sectionRefs.current[tocIndex] = el;
                }}
                data-testid={tocIndex >= 0 ? `article-section-${tocIndex}` : undefined}
                className={
                  "scroll-mt-24 " +
                  (sectionIndex === 0
                    ? ""
                    : "mt-9 border-t border-[var(--g-rule)] pt-9 md:mt-11 md:pt-11")
                }
              >
                {section.heading !== null && (
                  <h2 className="m-0 mb-3 text-[20px] font-bold leading-[1.25] text-foreground md:text-[23px]">
                    {section.heading}
                  </h2>
                )}
                {section.blocks.map((block, blockIndex) => {
                  const key = `${String(sectionIndex)}-${String(blockIndex)}`;
                  if (isCalloutBlock(block)) {
                    return (
                      <Note
                        key={key}
                        variant={block.callout}
                        text={block.text}
                        blockKey={key}
                      />
                    );
                  }
                  const isLead = sectionIndex === 0 && blockIndex === 0;
                  return (
                    <p
                      key={key}
                      className={
                        "m-0 text-foreground [&+p]:mt-5 " +
                        (isLead
                          ? "text-[17.5px] leading-[1.7] md:text-[19px]"
                          : "text-[16.5px] leading-[1.75] md:text-[17.5px]")
                      }
                    >
                      {withFigures(block.p, key)}
                    </p>
                  );
                })}
              </section>
            );
          })}
        </div>

        {/* Ask the assistant — the ink slab that closes the slip. Carries the
            article as a chat seed (composer opens with context; no auto-send). */}
        <button
          type="button"
          data-testid="article-ask"
          onClick={() =>
            navigate("/chat", {
              state: { seed: t("seedAsk", { title: article.title }) },
            })
          }
          className="group relative mt-12 block w-full overflow-hidden rounded-[var(--g-radius-lg)] bg-foreground p-6 text-left text-[var(--g-ground)] shadow-[var(--g-shadow-md)] transition-shadow hover:shadow-[var(--g-shadow-lg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background md:mt-16 md:p-8"
        >
          {/* `tone` puts `text-foreground` on the motif's own element, which on
              an ink slab paints ink-on-ink — the motif was invisible here for
              the whole life of this screen. The important flag is deliberate:
              the slab inverts the ground/ink pair, so the motif must too. */}
          <GMotif
            tone="ink"
            opacity={0.2}
            className="absolute inset-0 text-[var(--g-ground)]!"
          />
          <div className="relative max-w-[34ch]">
            <span className="font-display block text-[19px] leading-[1.2] md:text-[23px]">
              {t("askAbout")}
            </span>
            <span className="mt-2.5 block text-[14.5px] leading-[1.6] opacity-80 md:text-[15.5px]">
              {t("askLead")}
            </span>
            <span className="mt-5 inline-flex items-center gap-2 text-[14.5px] font-bold">
              {t("askCta")}
              <ArrowRight
                {...iconProps("md")}
                className="transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
              />
            </span>
          </div>
        </button>

        {/* What is next. Same ruled grid as the index, so the two screens read
            as one system. */}
        {(previous !== undefined || next !== undefined) && (
          <nav
            data-testid="article-siblings"
            className="mt-10 grid border-t border-[var(--g-rule-strong)] md:mt-14 md:grid-cols-2 [&>*+*]:border-t [&>*+*]:border-[var(--g-rule)] md:[&>*+*]:border-l md:[&>*+*]:border-t-0"
          >
            {previous !== undefined && (
              <Link
                to={`/library/${previous.slug}`}
                className="group flex flex-col gap-1.5 py-4 pr-4 transition-colors hover:bg-[var(--g-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:py-5"
              >
                <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.09em] text-[var(--g-ink-2)]">
                  <ArrowLeft {...iconProps("xs")} />
                  {t("prevLabel")}
                </span>
                <span className="text-[15.5px] font-semibold leading-snug text-foreground">
                  {previous.title}
                </span>
              </Link>
            )}
            {next !== undefined && (
              <Link
                to={`/library/${next.slug}`}
                className="group flex flex-col gap-1.5 py-4 transition-colors hover:bg-[var(--g-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:py-5 md:pl-6"
              >
                <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.09em] text-[var(--g-ink-2)]">
                  {t("nextLabel")}
                  <ArrowRight {...iconProps("xs")} />
                </span>
                <span className="text-[15.5px] font-semibold leading-snug text-foreground">
                  {next.title}
                </span>
              </Link>
            )}
          </nav>
        )}

        <Link
          to={LIBRARY_LIST_PATH}
          className="mt-8 inline-flex min-h-[44px] items-center gap-2 text-[14px] font-semibold text-[var(--g-ink-2)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft {...iconProps("sm")} />
          {t("allArticles")}
        </Link>
      </article>
    </div>
  );
}

export function ArticleScreen(): ReactElement {
  registerLibraryI18n();
  const { t, i18n } = useTranslation("library");
  const navigate = useNavigate();
  const { slug = "" } = useParams<{ slug: string }>();
  const lang = resolveKbLanguage(i18n.language);
  const [progress, setProgress] = useState(0);

  const { data, isPending, isError, error, refetch } = useArticle(slug, lang);
  // Same query key the index screen fills, so this is usually a cache read. It
  // powers the "article N of M" measure and the prev/next links — real
  // wayfinding from data we already have, never invented.
  const { data: list } = useArticleList(lang);

  const handleProgress = useCallback((value: number): void => {
    setProgress(value);
  }, []);

  function retry(): void {
    void refetch().then((result) => {
      if (result.isError) {
        notify.error(t("errorTitle"), {
          description: t("errorBody"),
          action: { label: t("retry"), onClick: retry },
          id: `library-article-${slug}`,
        });
      }
    });
  }

  if (isPending || (data === undefined && !isError)) {
    return (
      <ArticleChrome title={null} progress={0} showProgress={false}>
        <ArticleSkeleton />
      </ArticleChrome>
    );
  }

  // `data === undefined` here implies a failed fetch; fold it into the error
  // branch so the success branch below is strictly typed.
  if (isError || data === undefined) {
    // A 404 (unknown slug) is an expected outcome → friendly not-found state
    // with a link back to the index. Any other failure → retryable error.
    const isNotFound = error instanceof ApiError && error.status === 404;
    if (isNotFound) {
      return (
        <ArticleChrome title={null} progress={0} showProgress={false}>
          <ArticleSlip
            testId="article-not-found"
            role="status"
            motif
            title={t("notFoundTitle")}
            body={t("notFoundBody")}
            action={
              <Button
                variant="outline"
                leading="chevL"
                onClick={() => navigate(LIBRARY_LIST_PATH)}
                data-testid="article-not-found-cta"
              >
                {t("notFoundCta")}
              </Button>
            }
          />
        </ArticleChrome>
      );
    }
    return (
      <ArticleChrome title={null} progress={0} showProgress={false}>
        <ArticleSlip
          testId="article-error"
          role="alert"
          title={t("errorTitle")}
          body={t("errorBody")}
          action={
            <Button variant="outline" onClick={retry} data-testid="article-retry">
              {t("retry")}
            </Button>
          }
        />
      </ArticleChrome>
    );
  }

  return (
    <ArticleChrome title={data.title} progress={progress} showProgress>
      <ArticleBody
        article={data}
        siblings={list?.articles ?? []}
        onProgress={handleProgress}
      />
    </ArticleChrome>
  );
}
